import { Typography, makeStyles } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import {
  formatUptime,
  STATUS_COLOUR,
} from '../catalog/component/serviceStatus';
import {
  formatDaysAgo,
  formatLength,
  formatMonth,
  formatWindow,
  incidentHref,
  incidentName,
  minutesBetween,
  severityColour,
  stateLabel,
  type IncidentGroup,
  type Maintenance,
  type OverviewIncident,
  type Sla,
} from './overview';
import { StatusCard } from './StatusCard';

/** How many resolved incidents the card lists before it points to the rest. */
const RECENT_SHOWN = 5;

const useStyles = makeStyles(theme => ({
  list: { listStyle: 'none', margin: 0, padding: theme.spacing(0.5, 2) },
  item: {
    display: 'grid',
    gridTemplateColumns: '10px minmax(0, 1fr)',
    columnGap: theme.spacing(1.25),
    padding: theme.spacing(1, 0),
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: '0.45em',
  },
  what: {
    fontSize: '0.875rem',
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  meta: {
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  body: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    overflowWrap: 'anywhere',
  },
  muted: { color: 'var(--ui-text-muted)' },
  more: {
    padding: theme.spacing(0, 2, 1.5),
    fontSize: '0.8125rem',
  },
}));

const IncidentItem = ({
  incident,
  group,
  now,
}: {
  /** The incident the entry names: for a group, the one heard from last. */
  incident: OverviewIncident;
  /** The open incidents alike this entry stands for, when more than one. */
  group?: IncidentGroup;
  now: number;
}) => {
  const classes = useStyles();
  const open = incident.resolvedAt === null;
  const lengthOf = (one: OverviewIncident) =>
    formatLength(
      minutesBetween(
        one.startedAt,
        one.resolvedAt === null ? now : new Date(one.resolvedAt).getTime(),
      ),
    );
  // A group has run as long as its oldest member.
  const length = lengthOf(group?.oldest ?? incident);
  const name = incidentName(incident);
  return (
    <li className={classes.item}>
      <span
        className={classes.dot}
        style={{
          background: open
            ? severityColour(incident.severity)
            : STATUS_COLOUR.no_data,
        }}
        aria-hidden
      />
      <div>
        <div className={classes.what}>
          <Link to={incidentHref(incident)}>{name}</Link>
        </div>
        <div className={classes.meta}>
          {open
            ? `${stateLabel(incident.state)} · ${length}`
            : `Resolved · ${formatDaysAgo(
                incident.resolvedAt!,
                now,
              )} · ${length}`}
          {!incident.public && ' · internal'}
        </div>
        {group && group.count > 1 && (
          <div className={classes.meta}>
            {group.count} open alike:{' '}
            {group.incidents.map((one, index) => (
              <span key={one.id}>
                {index > 0 && ' · '}
                <Link
                  to={incidentHref(one)}
                  aria-label={`${name}, ${index + 1} of ${
                    group.count
                  }, open for ${lengthOf(one)}`}
                >
                  {lengthOf(one)}
                </Link>
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

/**
 * The open incidents, those alike as one entry, then those resolved in the
 * window, newest first, each leading to the incident (specs/status-page.md
 * AC-4). Unlike the banners, the card lists every open one.
 */
export const IncidentsCard = ({
  open,
  recent,
  windowDays,
  historyUrl,
  now,
}: {
  open: IncidentGroup[];
  recent: OverviewIncident[];
  windowDays: number;
  historyUrl: string;
  now: number;
}) => {
  const classes = useStyles();
  const shown = recent.slice(0, RECENT_SHOWN);
  return (
    <StatusCard
      id="status-incidents"
      title="Incidents"
      action={<Link to={historyUrl}>History</Link>}
    >
      {open.length + recent.length === 0 ? (
        <Typography className={`${classes.body} ${classes.muted}`}>
          No incident in the last {windowDays} days.
        </Typography>
      ) : (
        <>
          <ul className={classes.list} aria-labelledby="status-incidents">
            {open.map(group => (
              <IncidentItem
                key={group.oldest.id}
                incident={group.current}
                group={group}
                now={now}
              />
            ))}
            {shown.map(incident => (
              <IncidentItem key={incident.id} incident={incident} now={now} />
            ))}
          </ul>
          {recent.length > shown.length && (
            <Typography className={classes.more}>
              <Link to={historyUrl}>
                {recent.length - shown.length} more resolved in the last{' '}
                {windowDays} days
              </Link>
            </Typography>
          )}
        </>
      )}
    </StatusCard>
  );
};

/**
 * The maintenance planned and not over, soonest first, each with its window in
 * Berlin time and the component it affects (specs/status-page.md AC-5).
 */
export const MaintenanceCard = ({
  maintenance,
}: {
  maintenance: Maintenance[];
}) => {
  const classes = useStyles();
  return (
    <StatusCard id="status-maintenance" title="Scheduled maintenance">
      {maintenance.length === 0 ? (
        <Typography className={`${classes.body} ${classes.muted}`}>
          No maintenance is scheduled.
        </Typography>
      ) : (
        <ul className={classes.list} aria-labelledby="status-maintenance">
          {maintenance.map(window => (
            <li key={window.id} className={classes.item}>
              <span
                className={classes.dot}
                style={{ background: STATUS_COLOUR.maintenance }}
                aria-hidden
              />
              <div>
                <div className={classes.what}>
                  <Link to={window.adminUrl}>{window.title}</Link>
                </div>
                <div className={classes.meta}>
                  {formatWindow(window.startsAt, window.endsAt)}
                  {window.impact && ` · ${window.impact}`}
                </div>
                <div className={classes.meta}>
                  {window.service
                    ? `Affects: ${window.service.name}`
                    : 'Affects: no component named'}
                  {!window.public && ' · internal'}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </StatusCard>
  );
};

/**
 * The month's SLA so far against its target, as the status page reckons it,
 * and the report it publishes (specs/status-page.md AC-6). An SLA is promised
 * to customers, so the status page reckons it over the customer-facing
 * services in either view.
 */
export const SlaCard = ({
  sla,
  internalView,
  reportUrl,
}: {
  sla: Sla;
  internalView: boolean;
  reportUrl: string;
}) => {
  const classes = useStyles();
  let figure: JSX.Element;
  if (sla.uptimePct === null) {
    figure = (
      <Typography variant="body2">
        No customer-facing service was measured this month, so there is no SLA
        figure.
      </Typography>
    );
  } else {
    figure = (
      <Typography variant="body2">
        Customer-facing: <strong>{formatUptime(sla.uptimePct)}</strong> against
        a {sla.targetPct} % target — {sla.met ? 'met' : 'missed'}. Averaged over{' '}
        {sla.measuredServices}{' '}
        {sla.measuredServices === 1 ? 'service' : 'services'}
        {sla.unmeasuredServices > 0 &&
          `; ${sla.unmeasuredServices} not measured this month ${
            sla.unmeasuredServices === 1 ? 'is' : 'are'
          } left out`}
        .
      </Typography>
    );
  }
  return (
    <StatusCard
      id="status-sla"
      title="SLA this month"
      aside={formatMonth(sla.month)}
    >
      <div className={classes.body}>
        {figure}
        {internalView && (
          <Typography variant="body2" className={classes.muted}>
            An SLA is promised to customers, so the internal services are not in
            it.
          </Typography>
        )}
        <Typography variant="body2" className={classes.muted}>
          <Link to={reportUrl}>SLA report (PDF)</Link> — customer-facing
          services, last 90 days.
        </Typography>
      </div>
    </StatusCard>
  );
};
